/* The Android share sheet's landing.

   The manifest's share_target points at GET /share?title&text&url, which the
   server answers with the ordinary app shell. Everything else happens here.

   Two rules shape this:

   - **Capture before the router runs.** The share arrives as query params on a
     path that is not a workspace, so the router would normalise it away before
     anyone read it. The capture is therefore a module-evaluation side effect
     rather than a function someone remembers to call first: ES modules evaluate
     their dependencies before their own body, so importing this anywhere in
     g-shell's graph guarantees it beats `customElements.define` and the
     `router.start()` that follows. It also rewrites the URL to the app root, so
     a refresh cannot re-fire the same share.
   - **Submit after there is a session.** /api/share is identified by cookie, so
     a share arriving while signed out has to survive the sign-in. The payload
     sits in memory until the shell says it has an identity — which is also why
     this is a module-level holder rather than a promise chained off capture.

   Memory only, deliberately: a share that a reload discards is a share the user
   can send again, and persisting it risks re-submitting something they didn't
   mean to save. */

import { apiFetch, isSignedIn } from './api.js';
import { homeUrl } from './router.js';

export interface SharePayload {
  url: string;
  text: string;
  title: string;
}

/** Result of a submit, for whatever chooses to tell the user about it. */
export type ShareOutcome =
  | { ok: true }
  | { ok: false; reason: string };

let pending: SharePayload | null = null;

/** Whether a share is waiting to be submitted. */
export const hasPendingShare = (): boolean => pending !== null;

/** Read a share off the URL, if this load is one, and clear it from the bar. */
function capture(): void {
  if (location.pathname !== '/share') return;
  const q = new URLSearchParams(location.search);
  pending = {
    url: q.get('url') ?? '',
    text: q.get('text') ?? '',
    title: q.get('title') ?? '',
  };
  // Replace, not push: /share is a landing, not somewhere to go Back to.
  history.replaceState({}, '', homeUrl());
}

capture();

/**
 * Submit the captured share, if there is one. One-shot — the payload is cleared
 * before the request goes out, so a second call (or a re-render) cannot double
 * submit the same reel.
 *
 * Returns null when there was nothing pending, so a caller can tell "no share"
 * apart from "share failed".
 */
export async function submitPendingShare(): Promise<ShareOutcome | null> {
  const payload = pending;
  if (!payload) return null;
  pending = null;
  try {
    const res = await apiFetch<{ status?: string }>('/api/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!isSignedIn(res)) return { ok: false, reason: 'Sign in to save a shared reel.' };
    return { ok: true };
  } catch (e) {
    // The server's 400 detail is the useful one ("No instagram.com/reel/... URL
    // in the shared content") — shared text often isn't a reel at all.
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
}
